import { useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useRecoilValue } from 'recoil';
import { useAudio } from '@chainlit/react-client';
import { activeAudioButtonState } from '@/state/chat';
import { useTheme } from '@/components/ThemeProvider';
import { hslToHex } from '@/lib/utils';

/**
 * Full-screen overlay with dynamic edge animations for Realtime Assistant mode.
 * Creates glowing, voice-reactive bars on left and right edges of the screen.
 */
export default function RealtimeVoiceOverlay() {
  const { audioConnection, wavRecorder, isAiSpeaking } = useAudio();
  const activeButton = useRecoilValue(activeAudioButtonState);
  const { variant } = useTheme();
  
  const leftCanvasRef = useRef<HTMLCanvasElement>(null);
  const rightCanvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>();
  const timeRef = useRef(0);

  // Only show for assistant button
  const isActive = audioConnection === 'on' && activeButton === 'assistant';

  const colors = useMemo(() => {
    return {
      cyan: '#06b6d4',
      violet: '#8b5cf6',
      blue: '#3b82f6',
      pink: '#ec4899',
      teal: '#14b8a6',
    };
  }, [variant]);

  useEffect(() => {
    if (!isActive) return;

    const leftCanvas = leftCanvasRef.current;
    const rightCanvas = rightCanvasRef.current;
    if (!leftCanvas || !rightCanvas) return;

    const leftCtx = leftCanvas.getContext('2d');
    const rightCtx = rightCanvas.getContext('2d');
    if (!leftCtx || !rightCtx) return;

    const dpr = window.devicePixelRatio || 1;
    const edgeWidth = 80;
    
    const updateCanvasSize = () => {
      const height = window.innerHeight;
      
      leftCanvas.width = edgeWidth * dpr;
      leftCanvas.height = height * dpr;
      leftCanvas.style.width = `${edgeWidth}px`;
      leftCanvas.style.height = `${height}px`;
      
      rightCanvas.width = edgeWidth * dpr;
      rightCanvas.height = height * dpr;
      rightCanvas.style.width = `${edgeWidth}px`;
      rightCanvas.style.height = `${height}px`;
      
      leftCtx.scale(dpr, dpr);
      rightCtx.scale(dpr, dpr);
    };

    updateCanvasSize();
    window.addEventListener('resize', updateCanvasSize);

    // Get audio amplitude
    const getAmplitude = (): number => {
      try {
        if (wavRecorder?.recording) {
          const freqData = wavRecorder.getFrequencies('voice');
          if (freqData?.values?.length) {
            const avg = freqData.values.reduce((a: number, b: number) => a + b, 0) / freqData.values.length;
            return Math.min(1, avg * 2.5);
          }
        }
      } catch {
        // Fallback
      }
      return 0;
    };

    const render = () => {
      timeRef.current += 0.03;
      const t = timeRef.current;
      const height = window.innerHeight;
      
      const amplitude = getAmplitude();
      const dynamicIntensity = 0.3 + amplitude * 0.7;
      
      // Clear canvases
      leftCtx.clearRect(0, 0, edgeWidth, height);
      rightCtx.clearRect(0, 0, edgeWidth, height);

      // Number of segments
      const segments = 30;
      const segmentHeight = height / segments;

      // Draw on both sides
      [leftCtx, rightCtx].forEach((ctx, sideIndex) => {
        const isLeft = sideIndex === 0;
        
        for (let i = 0; i < segments; i++) {
          const y = i * segmentHeight;
          const normalizedY = i / segments;
          
          // Create flowing wave pattern
          const wave1 = Math.sin(normalizedY * Math.PI * 4 + t * 2) * 0.5 + 0.5;
          const wave2 = Math.sin(normalizedY * Math.PI * 6 - t * 1.5) * 0.3 + 0.5;
          const wave3 = Math.cos(normalizedY * Math.PI * 2 + t * 3) * 0.2 + 0.5;
          
          // Audio-reactive wave
          const audioWave = amplitude * Math.sin(normalizedY * Math.PI * 8 - t * 4) * 0.5;
          
          const combinedWave = (wave1 + wave2 + wave3) / 3 + audioWave;
          const barWidth = Math.max(5, combinedWave * edgeWidth * 0.8 * dynamicIntensity);
          
          // Color cycling
          const colorPhase = (normalizedY + t * 0.2) % 1;
          const colorArray = [colors.cyan, colors.violet, colors.blue, colors.pink, colors.teal];
          const colorIndex = Math.floor(colorPhase * colorArray.length);
          const nextColorIndex = (colorIndex + 1) % colorArray.length;
          const colorLerp = (colorPhase * colorArray.length) % 1;
          
          // Create gradient for each bar
          const gradient = isLeft 
            ? ctx.createLinearGradient(0, y, barWidth, y)
            : ctx.createLinearGradient(edgeWidth, y, edgeWidth - barWidth, y);
          
          // Clamp alpha to valid range [0, 1]
          const alpha = Math.min(1, Math.max(0, isAiSpeaking 
            ? 0.6 + combinedWave * 0.4 
            : 0.3 + combinedWave * 0.3));
          
          const alphaHex = Math.floor(alpha * 255).toString(16).padStart(2, '0');
          const alphaHex2 = Math.floor(Math.min(1, alpha * 0.7) * 255).toString(16).padStart(2, '0');
          
          gradient.addColorStop(0, `${colorArray[colorIndex]}${alphaHex}`);
          gradient.addColorStop(0.5, `${colorArray[nextColorIndex]}${alphaHex2}`);
          gradient.addColorStop(1, 'transparent');
          
          ctx.fillStyle = gradient;
          
          // Draw rounded bar
          const barX = isLeft ? 0 : edgeWidth - barWidth;
          const cornerRadius = Math.min(4, segmentHeight / 3);
          
          ctx.beginPath();
          if (isLeft) {
            ctx.moveTo(0, y);
            ctx.lineTo(barWidth - cornerRadius, y);
            ctx.quadraticCurveTo(barWidth, y, barWidth, y + cornerRadius);
            ctx.lineTo(barWidth, y + segmentHeight - cornerRadius);
            ctx.quadraticCurveTo(barWidth, y + segmentHeight, barWidth - cornerRadius, y + segmentHeight);
            ctx.lineTo(0, y + segmentHeight);
          } else {
            ctx.moveTo(edgeWidth, y);
            ctx.lineTo(edgeWidth - barWidth + cornerRadius, y);
            ctx.quadraticCurveTo(edgeWidth - barWidth, y, edgeWidth - barWidth, y + cornerRadius);
            ctx.lineTo(edgeWidth - barWidth, y + segmentHeight - cornerRadius);
            ctx.quadraticCurveTo(edgeWidth - barWidth, y + segmentHeight, edgeWidth - barWidth + cornerRadius, y + segmentHeight);
            ctx.lineTo(edgeWidth, y + segmentHeight);
          }
          ctx.closePath();
          ctx.fill();
        }
        
        // Add glow effect overlay
        const glowGradient = isLeft
          ? ctx.createLinearGradient(0, 0, edgeWidth, 0)
          : ctx.createLinearGradient(edgeWidth, 0, 0, 0);
        
        const glowAlpha = isAiSpeaking ? 0.15 : 0.08;
        glowGradient.addColorStop(0, `${colors.cyan}${Math.floor(glowAlpha * 255).toString(16).padStart(2, '0')}`);
        glowGradient.addColorStop(1, 'transparent');
        
        ctx.fillStyle = glowGradient;
        ctx.fillRect(0, 0, edgeWidth, height);
      });

      animationRef.current = requestAnimationFrame(render);
    };

    render();

    return () => {
      window.removeEventListener('resize', updateCanvasSize);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isActive, wavRecorder, isAiSpeaking, colors]);

  if (!isActive) return null;

  return createPortal(
    <div className="fixed inset-0 pointer-events-none z-[9999]">
      {/* Left edge */}
      <canvas
        ref={leftCanvasRef}
        className="absolute left-0 top-0 h-full"
        style={{ filter: 'blur(1px)' }}
      />
      {/* Right edge */}
      <canvas
        ref={rightCanvasRef}
        className="absolute right-0 top-0 h-full"
        style={{ filter: 'blur(1px)' }}
      />
      
      {/* Top gradient fade */}
      <div 
        className="absolute top-0 left-0 right-0 h-24 pointer-events-none"
        style={{
          background: `linear-gradient(to bottom, rgba(6, 182, 212, 0.1), transparent)`,
        }}
      />
      
      {/* Bottom gradient fade */}
      <div 
        className="absolute bottom-0 left-0 right-0 h-24 pointer-events-none"
        style={{
          background: `linear-gradient(to top, rgba(139, 92, 246, 0.1), transparent)`,
        }}
      />
    </div>,
    document.body
  );
}
